import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, Spinner, EmptyState, JobIdentity } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Upload, Loader2, FileText, Trash2, Plus, AlertTriangle, Eye, ExternalLink } from "lucide-react";

const TABS = ["OCR Review", "Active", "Partial", "Complete", "Problem", "VOID", "Closed"];

export default function SpkInbox() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("OCR Review");
  const [docs, setDocs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [review, setReview] = useState(null); // {id, extracted}
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dup, setDup] = useState(null);
  const [preview, setPreview] = useState(null); // {loading?, url?, type, fname?, error?}
  const [picker, setPicker] = useState(false);
  const [pickerDocs, setPickerDocs] = useState(null);

  const fileType = (fname) => {
    const ext = (fname || "").split(".").pop().toLowerCase();
    if (ext === "pdf") return "pdf";
    if (["jpg", "jpeg", "png", "webp"].includes(ext)) return "image";
    return "other";
  };
  const openPreview = async (fname) => {
    if (!fname) { setPreview({ error: true }); return; }
    setPreview({ loading: true, type: fileType(fname), fname });
    try {
      const res = await api.get(`/files/${fname}`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(res.data), type: fileType(fname), fname });
    } catch (err) {
      setPreview({ error: true, fname });
    }
  };
  const closePreview = () => setPreview((p) => { if (p?.url) URL.revokeObjectURL(p.url); return null; });
  const openPicker = async () => {
    setPicker(true); setPickerDocs(null);
    try { const r = await api.get("/spk"); setPickerDocs(r.data); } catch { setPickerDocs([]); }
  };

  const load = () => {
    setLoading(true);
    if (tab === "OCR Review") {
      api.get("/spk").then((r) => setDocs(r.data)).finally(() => setLoading(false));
    } else {
      api.get(`/jobs?status=${tab}`).then((r) => setJobs(r.data)).finally(() => setLoading(false));
    }
  };
  useEffect(load, [tab]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/spk/upload", fd);
      openReview(data);
      setTab("OCR Review");
      toast.success("OCR selesai. Silakan review.");
    } catch (err) {
      toast.error(apiError(err, "Upload gagal"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openReview = (doc) => {
    const ex = doc.extracted || {};
    setReview(doc);
    setForm({
      nopol: ex.nopol || "", claim_number: ex.claim_number || "", spk_number: ex.spk_number || "",
      customer_name: ex.customer_name || "", vehicle_brand: ex.vehicle_brand || "",
      vehicle_type: ex.vehicle_type || "", insurance: ex.insurance || "", spk_date: ex.spk_date || "",
      parts: (ex.parts || []).map((p) => ({
        part_number: p.part_number || "", part_name: p.part_name || "",
        qty: p.qty || 1, confidence: p.confidence ?? 0,
      })),
    });
  };

  const updatePart = (i, k, v) => {
    const parts = [...form.parts];
    parts[i] = { ...parts[i], [k]: v };
    setForm({ ...form, parts });
  };
  const addPart = () => setForm({ ...form, parts: [...form.parts, { part_number: "", part_name: "", qty: 1, confidence: 100 }] });
  const delPart = (i) => setForm({ ...form, parts: form.parts.filter((_, x) => x !== i) });

  const confirm = async (force = false) => {
    if (!form.nopol.trim()) return toast.error("Nopol wajib diisi");
    if (form.parts.length === 0) return toast.error("Minimal 1 part");
    setSaving(true);
    try {
      const { data } = await api.post(`/spk/${review.id}/confirm`, { ...form, force });
      if (data.possible_duplicate) { setDup(data.existing); setSaving(false); return; }
      toast.success(`SPK dikonfirmasi — ${data.job_number}`);
      setReview(null); setForm(null); setDup(null);
      navigate(`/jobs/${data.job_id}`);
    } catch (err) {
      toast.error(apiError(err, "Gagal konfirmasi"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="SPK Inbox" subtitle="Upload SPK, review hasil AI OCR, lalu konfirmasi menjadi Required Parts.">
        <Button variant="outline" onClick={openPicker} data-testid="view-original-spk-btn" className="border-[rgba(0,200,255,0.45)] text-[var(--neon-cyan)]">
          <Eye size={16} className="mr-2" /> View Original SPK
        </Button>
        <label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,image/*" className="hidden" onChange={onUpload} data-testid="spk-upload-input" />
          <Button asChild className="bg-gold text-white cursor-pointer" data-testid="spk-upload-btn">
            <span>{uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />} Upload SPK</span>
          </Button>
        </label>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="flex-wrap h-auto">
          {TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`spk-tab-${t}`}>{t}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {loading ? <Spinner /> : tab === "OCR Review" ? (
        docs.length === 0 ? <EmptyState title="Belum ada SPK menunggu review" subtitle="Upload SPK untuk memulai." icon={FileText} /> : (
          <div className="grid gap-3">
            {docs.map((d) => (
              <Card key={d.id} className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><FileText size={18} className="text-amber-600" /></div>
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">
                      <span className="font-mono-data">{d.extracted?.claim_number || "NO CLAIM"}</span>
                      <span className="text-gray-400"> — </span>
                      <span className="font-mono-data">{d.extracted?.nopol || "NOPOL?"}</span>
                      <span className="text-gray-400"> — </span>
                      <span>{d.extracted?.vehicle_brand || "-"}</span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">{d.extracted?.vehicle_type || ""}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {d.extracted?.customer_name ? `${d.extracted.customer_name} • ` : ""}
                      {d.extracted?.insurance || "—"} • {(d.extracted?.parts || []).length} part • {new Date(d.created_at).toLocaleString("id-ID")}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => openReview(d)} data-testid={`review-${d.id}`}>Review</Button>
              </Card>
            ))}
          </div>
        )
      ) : (
        jobs.length === 0 ? <EmptyState title={`Tidak ada job ${tab}`} icon={FileText} /> : (
          <div className="grid gap-3">
            {jobs.map((j) => <JobRow key={j.id} job={j} onClick={() => navigate(`/jobs/${j.id}`)} />)}
          </div>
        )
      )}

      <Dialog open={!!review} onOpenChange={(o) => !o && (setReview(null), setForm(null))}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Review Hasil OCR SPK</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="Nopol *" value={form.nopol} onChange={(v) => setForm({ ...form, nopol: v })} testId="f-nopol" />
                <Field label="No Claim" value={form.claim_number} onChange={(v) => setForm({ ...form, claim_number: v })} />
                <Field label="No SPK" value={form.spk_number} onChange={(v) => setForm({ ...form, spk_number: v })} />
                <Field label="Customer" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} />
                <Field label="Merk" value={form.vehicle_brand} onChange={(v) => setForm({ ...form, vehicle_brand: v })} />
                <Field label="Type" value={form.vehicle_type} onChange={(v) => setForm({ ...form, vehicle_type: v })} />
                <Field label="Insurance" value={form.insurance} onChange={(v) => setForm({ ...form, insurance: v })} />
                <Field label="Tanggal SPK" value={form.spk_date} onChange={(v) => setForm({ ...form, spk_date: v })} placeholder="YYYY-MM-DD" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Required Parts</div>
                  <Button size="sm" variant="outline" onClick={addPart} data-testid="add-part"><Plus size={14} className="mr-1" />Tambah</Button>
                </div>
                <div className="space-y-2">
                  {form.parts.map((p, i) => {
                    const low = (p.confidence ?? 0) < 70;
                    return (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center">
                        <Input className="col-span-3 h-9 font-mono-data" placeholder="Part Number" value={p.part_number} onChange={(e) => updatePart(i, "part_number", e.target.value)} data-testid={`part-num-${i}`} />
                        <Input className="col-span-5 h-9" placeholder="Nama Part" value={p.part_name} onChange={(e) => updatePart(i, "part_name", e.target.value)} data-testid={`part-name-${i}`} />
                        <Input className="col-span-1 h-9" type="number" value={p.qty} onChange={(e) => updatePart(i, "qty", Number(e.target.value))} data-testid={`part-qty-${i}`} />
                        <div className="col-span-2 flex items-center">
                          {low ? (
                            <span className="text-[10px] font-semibold text-amber-700 flex items-center gap-1"><AlertTriangle size={12} />NEED REVIEW</span>
                          ) : (
                            <span className="text-[11px] font-semibold text-emerald-600">{p.confidence}%</span>
                          )}
                        </div>
                        <button className="col-span-1 text-gray-400 hover:text-red-500" onClick={() => delPart(i)}><Trash2 size={16} /></button>
                      </div>
                    );
                  })}
                  {form.parts.length === 0 && <div className="text-sm text-gray-400 py-4 text-center">Belum ada part. Tambah manual.</div>}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => openPreview(review?.file)} data-testid="review-view-original-btn" className="border-[rgba(0,200,255,0.45)] text-[var(--neon-cyan)] sm:mr-auto">
              <Eye size={16} className="mr-2" /> View Original SPK
            </Button>
            <Button variant="outline" onClick={() => (setReview(null), setForm(null))}>Batal</Button>
            <Button onClick={() => confirm(false)} disabled={saving} className="bg-gray-900" data-testid="confirm-spk-btn">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Confirm SPK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dup} onOpenChange={(o) => !o && setDup(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><AlertTriangle size={18} className="text-amber-500" />POSSIBLE DUPLICATE SPK</DialogTitle></DialogHeader>
          {dup && (
            <div className="text-sm space-y-1 bg-amber-50 rounded-lg p-3">
              <div>Sudah ada Job dengan identitas serupa:</div>
              <div>Nopol: <b className="font-mono-data">{dup.nopol}</b></div>
              <div>No Claim: <b className="font-mono-data">{dup.claim_number || "—"}</b></div>
              <div>No SPK: <b className="font-mono-data">{dup.spk_number || "—"}</b></div>
              <div>Status: <b>{dup.status}</b> • {dup.job_number}</div>
            </div>
          )}
          <p className="text-sm text-gray-500">Pilih tindakan. Force Create hanya untuk Owner.</p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDup(null)} data-testid="dup-cancel">Cancel</Button>
            <Button variant="outline" onClick={() => navigate(`/jobs/${dup.job_id}`)} data-testid="dup-open-existing">Open Existing SPK</Button>
            {user.role === "owner" && (
              <Button className="bg-red-600 hover:bg-red-700" onClick={() => { setDup(null); confirm(true); }} data-testid="dup-force-create">Force Create New</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Picker: choose which OCR Review SPK original to open */}
      <Dialog open={picker} onOpenChange={(o) => !o && setPicker(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-head">Pilih SPK untuk dilihat</DialogTitle></DialogHeader>
          {pickerDocs === null ? <Spinner /> : pickerDocs.length === 0 ? (
            <div className="text-sm text-gray-400 py-6 text-center" data-testid="picker-empty">Belum ada SPK untuk ditampilkan.</div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {pickerDocs.map((d) => (
                <button key={d.id} data-testid={`picker-doc-${d.id}`}
                  onClick={() => { setPicker(false); openPreview(d.file); }}
                  className="w-full text-left rounded-lg border border-[rgba(0,200,255,0.25)] p-3 hover:bg-[rgba(0,200,255,0.08)] flex items-center gap-3 transition-colors">
                  <FileText size={18} className="text-[var(--neon-cyan)] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-white truncate">{d.extracted?.nopol || "Nopol belum terbaca"}</div>
                    <div className="text-xs text-gray-500">{(d.extracted?.parts || []).length} part • {new Date(d.created_at).toLocaleString("id-ID")}</div>
                  </div>
                  <Eye size={16} className="text-[var(--neon-cyan)]" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Original SPK preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><FileText size={18} className="text-[var(--neon-cyan)]" />Original SPK</DialogTitle>
          </DialogHeader>
          {preview?.loading ? <Spinner /> : preview?.error ? (
            <div className="text-sm text-gray-400 py-10 text-center" data-testid="preview-unavailable">File original SPK tidak tersedia.</div>
          ) : preview?.url ? (
            <div className="flex-1 min-h-0" data-testid="spk-preview-content">
              {preview.type === "pdf" ? (
                <iframe title="SPK PDF" src={preview.url} className="w-full h-[70vh] rounded-lg border border-[rgba(0,200,255,0.25)] bg-white" />
              ) : preview.type === "image" ? (
                <div className="w-full h-[70vh] overflow-auto rounded-lg border border-[rgba(0,200,255,0.25)] flex items-center justify-center bg-black/40">
                  <img src={preview.url} alt="SPK" className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="text-sm text-gray-400 py-10 text-center">Format tidak didukung untuk preview.</div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            {preview?.url && (
              <a href={preview.url} target="_blank" rel="noreferrer" data-testid="preview-new-tab">
                <Button variant="outline" className="border-[rgba(0,200,255,0.45)] text-[var(--neon-cyan)]"><ExternalLink size={16} className="mr-2" />Buka di tab baru</Button>
              </a>
            )}
            <Button variant="outline" onClick={closePreview} data-testid="preview-close-btn">Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</label>
      <Input className="mt-1 h-9" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

export function JobRow({ job, onClick }) {
  const s = job.parts_summary || {};
  const stats = [
    ["Required", s.required], ["Received", s.received], ["Remaining", s.remaining],
    ["Issued", s.issued], ["Installed", s.installed],
  ];
  return (
    <Card className="p-4 hover:shadow-sm transition-shadow cursor-pointer" onClick={onClick} data-testid={`job-row-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <JobIdentity job={job} />
          <div className="text-xs text-gray-500 mt-2">
            {job.vehicle_brand} {job.vehicle_type} {job.insurance ? `• ${job.insurance}` : ""} • {job.job_number}
          </div>
        </div>
        <StatusBadge status={job.status === "VOID" ? "PROBLEM" : job.status} />
      </div>
      {s.total ? (
        <div className="grid grid-cols-5 gap-1 mt-3 text-center border-t border-gray-100 pt-3">
          {stats.map(([l, v]) => (
            <div key={l}><div className="text-[10px] uppercase tracking-wide text-gray-400">{l}</div><div className="font-mono-data font-semibold text-gray-900 text-sm">{v ?? 0}</div></div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
